<script setup>
import dayjs from 'dayjs'
import { ref, computed, onMounted } from 'vue'

import { getI18n } from '@/plugins/i18n'

import { stringToColor, textColor, formatDayMonthDate } from '@/lib/utils'

import { usePlanningStore } from '@/stores/planning.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { serviceTypeEnum } from '@/stores/portal.store'

import { useIsUpdated, useClone } from '@/composables/object'
import { useDetectDevice } from '@/composables/device'
import { usePriceService } from '@/composables/price.service'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useChips } from '@/composables/chips'
import { resolveCurrencyRef, useCurrency } from '@/composables/currency'
import { currencyCode } from '@/composables/exchange-rate'
import { useTagDraft } from '@/composables/tag'
import { useNotify } from '@/composables/notify'

import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import InputDateSelector from '@/components/shared/InputDateSelector.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import VatSelect from '@/components/shared/VatSelect.vue'
import TagCreateDialog from '@/components/shared/TagCreateDialog.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  budgetDocument: {
    type: Object,
    required: true,
  },
  groupRowsBy: {
    type: [Number, null]
  },
})

const emit = defineEmits(['update', 'cancel'])
useNavigator(() => emit('cancel'))

const { isMobile } = useDetectDevice()

const i18n = getI18n()
const { notifyWarning } = useNotify()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()

const form = ref(null)
const editItem = ref(useClone(props.modelValue))
if (editItem.value.vat_type_ref && !editItem.value.vat_type_percentage) {
  editItem.value.vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === editItem.value.vat_type_ref)?.percentage
}
const listingItemFilter = ref('')

// Listing items already used by other rows of this quote: a service maps 1:1 to a
// listing item within a listing, so re-adding one warns before saving (mirrors the
// timeline drag&drop). Populated in onMounted.
const usedListingItemIds = ref(new Set())
const duplicateConfirmVisible = ref(false)

const { isUpdated } = useIsUpdated(editItem)
useEscapeClose(isUpdated, () => emit('cancel'))
const { getBudgetDocumentTagChips, getBudgetDocumentRowTypeChip } = useChips()
const { getListingItemAmounts, setPriceValue, setCostValue, setQuantityValue, setCostValorizationValue, setVatTypeValue, setTotalNoVatValue, setTotalVatValue, setTotalNoVatEnteredValue, setTotalVatEnteredValue, setDiscountValue, setChargePercentageValue } = usePriceService({ budgetDocument: props.budgetDocument })

// Grid width per breakpoint: `col(mobile, desktop)` — every row of controls adds up to 12.
const col = (mobile, desktop) => `col-${isMobile.value ? mobile : desktop}`

const isComment = computed(() => editItem.value.document_row_type === webSiteStore.documentRowTypeEnum.COMMENT)

// Extra rows report an expense (hotel, travel, ...): no unit of measure, planned amount,
// charge or discount — just how many, how much and the VAT.
const isExtra = computed(() => editItem.value.document_row_type === webSiteStore.documentRowTypeEnum.EXTRA)

// Listing-item rows take their dates from the allocation events, so the field is only
// meaningful on extra/comment rows, and only once the row exists.
const showDate = computed(
  () => !!editItem.value.id && editItem.value.document_row_type !== webSiteStore.documentRowTypeEnum.LISTING_ITEM,
)

// Second row of the main section: date + cost valorization + tags, filling 12.
const tagsDesktopCols = computed(() => 12 - (showDate.value ? 4 : 0) - (isComment.value ? 0 : 4))

const isUomTimeBased = computed(() => {
  const uom = webSiteStore.getLocalizedUnitsOfMeasure().find(u => u.id === editItem.value.unit_of_measure_ref)
  return uom?.is_time_based ?? false
})

// Serialized equipment and staff are single units: their quantity is always 1.
const selectedListingItem = computed(() =>
  planningStore.listingItems.find(li => li.id === editItem.value.listing_item) ?? null,
)
const isUnitaryService = computed(() => {
  const item = selectedListingItem.value
  return !!item && (item.service_is_serialized || item.service_type === serviceTypeEnum.STAFF)
})

onMounted(async () => {
  accountingStore.fetchBudgetTagsAsync({all: true}, true)
  // The selectable listing items are those of the quote's (budget document's)
  // rate-card. They are not loaded by the accounting flow, so fetch them here
  // scoped to that listing (mirrors ProjectEdit). Listings are needed for the
  // non-archived filter in availableListingItems.
  const listingId = props.budgetDocument.listing
  const [, , rowsRes] = await Promise.all([
    planningStore.listings.length ? Promise.resolve() : planningStore.fetchListingsAsync({ all: true }, true),
    listingId ? planningStore.fetchListingItemsAsync({ listing: listingId, all: true }, true) : Promise.resolve(),
    accountingStore.api.getBudgetDocumentRowsAsync({ budget_document: editItem.value.budget_document }),
  ])
  usedListingItemIds.value = new Set(
    (rowsRes?.data ?? [])
      .filter(row => row.id !== editItem.value.id && row.listing_item)
      .map(row => row.listing_item),
  )
})

const availableListingItems = computed(() => {
  const nonArchivedListingIds = new Set(planningStore.listings.filter(l => !l.archived).map(l => l.id))
  let items = planningStore.listingItems.filter(item => nonArchivedListingIds.has(item.listing))
  // The listing is mandatory on the quote, so only items in the quote's listing are selectable.
  const listingId = props.budgetDocument.listing ?? editItem.value.listing
  if (listingId) {
    items = items.filter(item => item.listing === listingId)
  }
  if (listingItemFilter.value) {
    items = items.filter(item => planningStore.getListingItemName(item).toLowerCase().indexOf(listingItemFilter.value.toLowerCase()) > -1)
  }
  return items.map(item => {
    return {
      ...item,
      name: planningStore.getListingItemName(item),
    }
  }).sort((a, b) => a.name.localeCompare(b.name))
})


const title = computed(() => {
  if (editItem.value.document_row_type === webSiteStore.documentRowTypeEnum.LISTING_ITEM) {
    const listingItem = planningStore.listingItems.find(li => li.id === editItem.value.listing_item)
    const name = editItem.value.listing ? listingItem?.name : listingItem?.full_name
    return `${name ? name : ''}`
  }
  if (editItem.value.document_row_type === webSiteStore.documentRowTypeEnum.EXTRA) {
    const category = accountingStore.getLocalizedBudgetDocumentRowCategories().find(c => c.id === editItem.value.budget_document_row_category_ref)
    return `${category?.name ? category?.name : ''}`
  }
  return editItem.value.description
})

// When a rate-card item is picked, pre-fill amount_per_unit with the project's
// planned duration converted into the item's (time-based) UoM. The conversion is
// computed on the BE. amount_per_unit must be set before setPrice/setCost so the
// totals include it (getTotalNoVatValue multiplies by amount_per_unit).
const computePlannedPerUnit = async (uom) => {
  if (!uom?.is_time_based) {
    return null
  }
  try {
    const res = await accountingStore.api.getBudgetDocumentPlannedPerUnitAsync(editItem.value.budget_document, uom.code)
    if (res.data?.amount_per_unit != null) {
      return Number(res.data.amount_per_unit)
    }
  } catch {
    // fall back to 1 (no computable date range or request failure)
  }
  return 1
}

const onListingItemSelected = async (value) => {
  const listingItem = planningStore.listingItems.find(li => li.id === value)
  if (!listingItem) {
    return
  }
  editItem.value.listing_item_text = listingItem.name
  editItem.value.unit_of_measure_ref = listingItem.unit_of_measure_ref
  const uom = webSiteStore.getLocalizedUnitsOfMeasure().find(u => u.id === listingItem.unit_of_measure_ref)
  editItem.value.amount_per_unit = await computePlannedPerUnit(uom)
  if (listingItem.service_is_serialized || listingItem.service_type === serviceTypeEnum.STAFF) {
    setQuantityValue(editItem.value, 1, editItem.value)
  }
  // Cost is always copied from the listing item (it drives the row cost/margin);
  // the price is the cost or the list price depending on the valorization mode.
  const listingAmounts = getListingItemAmounts(listingItem)
  setCostValue(editItem.value, listingAmounts.cost, editItem.value)
  setPriceValue(
    editItem.value,
    editItem.value.cost_valorization === accountingStore.costValorizationsEnum.COST ? listingAmounts.cost : listingAmounts.price,
    editItem.value,
  )
}

const onListingItemFilter = (val, update) => {
  if (val === '') {
    update(() => {
      listingItemFilter.value = ''
    })
    return
  }
  
  update(() => {
    listingItemFilter.value = val.toLowerCase()
  })
}

const currentCurrency = computed(() => webSiteStore.currencies.find(i => i.id === editItem.value.currency_ref) || webSiteStore.getDefaultCurrency())

const { getCurrencyString } = useCurrency()
const listingAmountHint = (amount) => {
  const listingCurrencyRef = resolveCurrencyRef(selectedListingItem.value?.currency_ref)
  if (amount == null || listingCurrencyRef === resolveCurrencyRef(props.budgetDocument.currency_ref)) {
    return undefined
  }
  const code = currencyCode(listingCurrencyRef)
  return code ? i18n.t('company.exchangeRates.inListingCurrency', { amount: getCurrencyString(amount, code) }) : undefined
}

const isDuplicateListingItem = computed(() =>
  editItem.value.document_row_type === webSiteStore.documentRowTypeEnum.LISTING_ITEM
  && !!editItem.value.listing_item
  && usedListingItemIds.value.has(editItem.value.listing_item),
)

const onUpsert = async (item) => {
  if (isDuplicateListingItem.value && isUnitaryService.value) {
    notifyWarning(i18n.t('warning.unitaryServiceAlreadyInProject'))
    return
  }
  if (isDuplicateListingItem.value) {
    duplicateConfirmVisible.value = true
    return
  }
  await persistRow(item)
}

const persistRow = async (item) => {
  const { data: saved } = await accountingStore.api.upsertBudgetDocumentRowAsync(item)
  emit('update', saved)
}

const confirmDuplicateSave = () => {
  duplicateConfirmVisible.value = false
  persistRow(editItem.value)
}

const onCancel = () => emit('cancel')

const setPrimaryBudgetTagsValue = (item, value) => {
  if (!item.tags) {
    item.tags = []
  }
  item.tags = value
}

const { tagDraft, openTagDraft, confirmTagDraftAsync } = useTagDraft(accountingStore.createBudgetTagAsync)
const addBudgetTag = (value, doneFn) => {
  const existingTag = accountingStore.getLocalizedBudgetTags().find(t => t.name.toLowerCase() === value.toLowerCase())
  if (existingTag) {
    doneFn(existingTag.id)
    return
  }
  openTagDraft(value, doneFn)
}
const addPrimaryBudgetTag = (value, doneFn) => addBudgetTag(value, doneFn)

const onDownloadRowFileAsync = async(e) => {
  if (e) {
    await accountingStore.api.downloadBudgetDocumentRowFileAsync(e.id, e.name)
  }
}

const fromPercentage = (value) => (value != null && value !== '' ? value / 100 : null)

const onChargePercentageChanged = (value) => setChargePercentageValue(editItem.value, fromPercentage(value), editItem.value)

const onDiscountChanged = (value) => setDiscountValue(editItem.value, fromPercentage(value), editItem.value)

</script>
<template>
  <q-card class="dialog-card-flex">
    <tag-create-dialog
      :draft="tagDraft"
      :kinds="accountingStore.getLocalizedBudgetTagKinds()"
      :location-kind="accountingStore.budgetTagKindEnum.LOCATION"
      @confirm="confirmTagDraftAsync"
      @cancel="tagDraft = null"
    />
    <navigate-back
      :text="title || '-'"
      @cancel="onCancel"
    >
      <div>
        <div class="text-left text-h6"> {{ title }} </div>
        <chip-template :values="[getBudgetDocumentRowTypeChip(editItem.document_row_type)]" :style="{'margin-left': 0}" />
        <chip-template :values="getBudgetDocumentTagChips(editItem.tags)" />
        <span v-if="editItem.date">
          <span v-if="groupRowsBy === accountingStore.budgetDocumentGroupRowsByEnum.DAY" class="caption q-ml-xs">{{ `${i18n.t('common.day')} ${dayjs(editItem.day + 1).date()}, ${dayjs(editItem.date).format('dddd')}` }}</span>
          <span v-else class="caption q-ml-xs text-grey-7"><q-icon name="mdi-calendar" class="q-mr-xs" />{{ `${formatDayMonthDate(editItem.date)}` }}</span>
        </span>
      </div>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none dialog-card-content">
      <q-form ref="form">
        <q-card-section class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-information-outline"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">
              {{ i18n.t('common.mainInfo') }}
            </span>
          </q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <q-select
              v-if="editItem.document_row_type === webSiteStore.documentRowTypeEnum.LISTING_ITEM"
              v-model="editItem.listing_item"
              :class="col(12, 12)"
              :label="i18n.t('common.listingItem')"
              option-value="id"
              option-label="name"
              :options="availableListingItems"
              :rules="[val => !!val || i18n.t('common.required')]"
              use-input
              :input-debounce="webSiteStore.getInputDebounce()"
              @update:model-value="onListingItemSelected"
              @filter="onListingItemFilter"
            />
            <q-select
              v-if="editItem.document_row_type === webSiteStore.documentRowTypeEnum.EXTRA"
              v-model="editItem.budget_document_row_category_ref"
              :class="col(12, 4)"
              :label="i18n.t('common.category')"
              option-value="id"
              option-label="name"
              :options="accountingStore.getLocalizedBudgetDocumentRowCategories()"
            />
            <q-input
              v-if="editItem.document_row_type !== webSiteStore.documentRowTypeEnum.LISTING_ITEM"
              v-model="editItem.description"
              :class="col(12, isComment ? 12 : 8)"
              :label="i18n.t('common.description')"
              autogrow
            />
            <InputDateSelector
              v-if="showDate"
              v-model="editItem.date"
              clearable
              :min="props.budgetDocument.start"
              :max="props.budgetDocument.end"
              :range="false"
              :class="col(12, 4)"
              :label="i18n.t('common.date')"
            />
            <q-select
              v-if="!isComment"
              :model-value="editItem.cost_valorization"
              :class="col(6, 4)"
              :label="i18n.t('common.costValorizationAlt')"
              option-value="id"
              option-label="name"
              :options="accountingStore.getLocalizedCostValorizations()"
              @update:model-value="(value) => setCostValorizationValue(editItem, value, editItem)"
            />
            <q-select
              :model-value="editItem.tags"
              :class="col(isComment ? 12 : 6, tagsDesktopCols)"
              :label="i18n.t('common.tag', 2)"
              :options="accountingStore.getLocalizedBudgetTags()"
              option-value="id"
              option-label="name"
              use-input
              use-chips
              multiple
              new-value-mode="add-unique"
              @new-value="addPrimaryBudgetTag"
              @update:model-value="(value) => setPrimaryBudgetTagsValue(editItem, value)"
            >
              <template #selected-item="chip">
                <q-chip
                  removable
                  size="sm"
                  :tabindex="chip.tabindex"
                  class="q-ma-none"
                  :color="chip.color ? chip.color : 'initial'"
                  :style="{ 'background-color': stringToColor(chip.opt.name), 'color': textColor(stringToColor(chip.opt.name)), 'margin': 0, 'margin-right': '.25rem' }"
                  @remove="chip.removeAtIndex(chip.index)"
                >
                  {{ chip.opt.name }}
                </q-chip>
              </template>
            </q-select>
          </div>
        </q-card-section>
        <q-card-section v-if="!isComment" class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-package-variant"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">
              {{ i18n.t('common.allocation') }}
            </span>
          </q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <q-input
              :model-value="editItem.quantity"
              :class="col(6, isUomTimeBased && !isExtra ? 4 : 6)"
              type="number"
              :label="i18n.t('common.units')"
              min="0"
              step="1"
              :readonly="isUnitaryService"
              @update:model-value="(value) => {
                setQuantityValue(editItem, value, editItem)
              }"
            />
            <q-select
              v-model="editItem.unit_of_measure_ref"
              :class="col(6, isUomTimeBased && !isExtra ? 4 : 6)"
              :label="i18n.t('common.per')"
              option-value="id"
              option-label="name"
              :options="webSiteStore.getLocalizedUnitsOfMeasure()"
              :readonly="!isExtra"
            />
            <q-input
              v-if="isUomTimeBased && !isExtra"
              :model-value="editItem.amount_per_unit"
              :class="col(12, 4)"
              type="number"
              :label="i18n.t(isUomTimeBased ? 'common.duration' : 'common.quantityAlt')"
              min="0"
              step="0.01"
              @update:model-value="(value) => {
                editItem.amount_per_unit = value
                setTotalNoVatValue(editItem, editItem)
                setTotalVatValue(editItem, editItem)
              }"
            />
          </div>
        </q-card-section>
        <q-card-section v-if="!isComment" class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-cash-multiple"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">
              {{ i18n.t('common.costAndPrice') }}
            </span>
          </q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <currency-input
              :model-value="editItem.cost"
              :class="col(6, 4)"
              :label="i18n.t('common.cost')"
              :suffix="currentCurrency.symbol"
              :hint="listingAmountHint(selectedListingItem?.cost)"
              @update:model-value="(value) => setCostValue(editItem, value, editItem)"
            />
            <currency-input
              :model-value="editItem.price"
              :class="col(6, 4)"
              :label="i18n.t('common.price')"
              :suffix="currentCurrency.symbol"
              :hint="listingAmountHint(editItem.cost_valorization === accountingStore.costValorizationsEnum.COST ? selectedListingItem?.cost : selectedListingItem?.price)"
              @update:model-value="(value) => setPriceValue(editItem, value, editItem)"
            />
            <vat-select
              :model-value="editItem.vat_type_ref"
              :class="col(12, 4)"
              @update:model-value="(value) => setVatTypeValue(editItem, value, editItem)"
            />
          </div>
        </q-card-section>
        <q-card-section v-if="!isComment && !isExtra" class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-label-percent-outline"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">
              {{ i18n.t('common.surchargeAndDiscount') }}
            </span></q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <q-input
              :model-value="editItem.charge_percentage != null ? editItem.charge_percentage * 100 : null"
              :class="col(6, 6)"
              type="number"
              clearable
              :disable="!!editItem.discount"
              :label="i18n.t('common.chargePercentageAlt')"
              min="0"
              step="1"
              suffix="%"
              @update:model-value="onChargePercentageChanged"
            />
            <q-input
              :model-value="editItem.discount != null ? editItem.discount * 100 : null"
              :class="col(6, 6)"
              type="number"
              clearable
              :disable="!!editItem.charge_percentage"
              :label="i18n.t('common.discount')"
              min="0"
              step="1"
              suffix="%"
              @update:model-value="onDiscountChanged"
            />
          </div>
        </q-card-section>
        <q-card-section v-if="!isComment" class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-cash-multiple"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">
              {{ i18n.t('common.total', 2) }}
            </span>
          </q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <currency-input
              :model-value="editItem.total_no_vat"
              :class="col(6, 6)"
              :label="i18n.t('common.totalNoVat')"
              :suffix="currentCurrency.symbol"
              @update:model-value="(value) => setTotalNoVatEnteredValue(editItem, value, editItem)"
            />
            <currency-input
              :model-value="editItem.total_vat"
              :class="col(6, 6)"
              :label="i18n.t('common.totalVat')"
              :suffix="currentCurrency.symbol"
              @update:model-value="(value) => setTotalVatEnteredValue(editItem, value, editItem)"
            />
          </div>
        </q-card-section>
        <q-card-section class="q-pt-none">
          <q-item-label header class="row items-center q-px-none q-pb-xs">
            <q-icon
              name="mdi-cash-multiple"
              class="q-mr-sm"
              color="primary"
              size="sm"
            />
            <span class="dialog-card-section-title">
              {{ i18n.t('common.other') }}
            </span>
          </q-item-label>
          <div class="q-ml-sm row q-col-gutter-sm">
            <q-input
              v-model="editItem.notes"
              autogrow
              :class="col(12, 12)"
              :label="i18n.t('common.note', 2)"
            />
            <file-url-edit
              class="col-12"
              bordered
              caption
              :model-value="editItem.row_files"
              :urls="editItem.row_urls"
              @update:model-value="editItem.row_files = $event"
              @update:urls="editItem.row_urls = $event"
              @download="onDownloadRowFileAsync"
            />
          </div>
        </q-card-section>
      </q-form>
    </q-card-section>
    <q-card-actions v-if="isUpdated" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="positive"
        :label="editItem.id ? i18n.t('common.update') : i18n.t('common.add')"
        @click="form.validate().then((success) => success ? onUpsert(editItem) : null)"
      />
    </q-card-actions>
  </q-card>

  <confirm-dialog
    v-model="duplicateConfirmVisible"
    :message="i18n.t('warning.serviceAlreadyInProjectConfirm')"
    icon="mdi-alert"
    icon-color="warning"
    @confirm="confirmDuplicateSave"
  />
</template>
<style lang="scss" scoped>
</style>
